/***************************************************************************************************
** useDataFetch
***************************************************************************************************/

import { useMemo, useRef, useReducer, useCallback, useEffect } from "react";

const FETCHING = Symbol("FETCHING");
const FETCHED = Symbol("FETCHED");
const FETCH_ERROR = Symbol("FETCH_ERROR");

const INVALID_PATH_SEGMENTS = [
  undefined,
  "undefined",
  null,
  "null",
  "",
];

const PUBLIC_ENDPOINTS = ["website", "external-link"];

const initialState = {
  status: "idle",
  error: null,
  data: [],
};

export default function useDataFetch(pathSegments = [], params = {}) {
  const path = useMemo(() => {
    const hasInvalidSegments = pathSegments.some((segment) => INVALID_PATH_SEGMENTS.includes(segment));
    if (hasInvalidSegments) return null;

    const isPublicEndpoint = PUBLIC_ENDPOINTS.includes(pathSegments[0]);
    if (!isPublicEndpoint && !window.appContext.householdId) return null;

    const urlParams = new URLSearchParams(params);

    if (window.appContext.householdId) {
      urlParams.set("household_id", window.appContext.householdId);
    }

    if (window.appContext.personId) {
      urlParams.set("person_id", window.appContext.personId);
    }

    const query = urlParams.toString();

    const relativePath = pathSegments.join("/");

    return query ? `/data/${relativePath}?${query}` : `/data/${relativePath}`;
  }, [pathSegments, params]);

  const dataCache = useRef({});

  const [state, dispatch] = useReducer((state, action) => {
    switch (action.type) {
    case FETCHING:
      return {
        ...initialState,
        status: "fetching",
      };
    case FETCHED:
      return {
        ...initialState,
        status: "fetched",
        data: action.payload,
      };
    case FETCH_ERROR:
      return {
        ...initialState,
        status: "error",
        error: action.payload,
      };
    default:
      return state;
    }
  }, initialState);

  /***************************************************************************************************
  ** __fetchData
  ***************************************************************************************************/

  const __fetchData = useCallback(async (abortSignal) => {
    if (!path) return;

    dispatch({ type: FETCHING });

    if (dataCache.current[path]) {
      const data = dataCache.current[path];
      dispatch({ type: FETCHED, payload: data });
    } else {
      try {
        const response = await fetch(path, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "Cache-Control": "no-cache, no-store",
            "Authorization-Token": window.appContext.authorizationToken,
          },
          signal: abortSignal,
        });

        if (!response.ok) throw new Error(response.statusText);

        const data = await response.json();

        dataCache.current[path] = data;

        dispatch({ type: FETCHED, payload: data });
      } catch (error) {
        if (error.name === "AbortError") return;

        dispatch({ type: FETCH_ERROR, payload: error.message });
      }
    }
  }, [path]);

  /***************************************************************************************************
  ** fetch
  ***************************************************************************************************/

  const fetchData = useCallback(() => {
    const abortController = new AbortController();
    __fetchData(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [__fetchData]);

  /***************************************************************************************************
  ** refetch
  ***************************************************************************************************/

  const refetchData = useCallback(() => {
    dataCache.current[path] = undefined;

    const abortController = new AbortController();
    __fetchData(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [path, __fetchData]);

  /***************************************************************************************************
  ** Run fetchData on mount
  ***************************************************************************************************/

  useEffect(() => {
    const abortController = new AbortController();
    __fetchData(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [__fetchData]);

  /**************************************************************************************************/

  return {
    ...state,
    isFetched: state.status === "fetched",
    isFetching: state.status === "fetching",
    fetch: fetchData,
    refetch: refetchData,
  };
}
